//@ts-check
import { useState, useCallback } from 'react';
import { t } from 'i18next';
import { updateGroupLocationTrackingViaAdmin } from '@/repository/OrganizationDAO';
import {
  ToastType,
  useToast,
} from '@/context_providers/toast_context/ToastContext';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import { ErrorStringKeys, StringKeys } from '@/resources/strings/StringKeys';
import Group from '@/models/Organization/Group';

/**
 * @typedef {Object} GroupLocationConfiguration
 * @property {boolean} saving - Whether a save operation is currently in progress
 * @property {(group: Group, allowLocationTracking: boolean) => Promise<{success: boolean} | undefined> } updateGroupLocationTracking - Function to update group location tracking setting
 */

/**
 * Custom hook for managing group location tracking configuration.
 * Handles API calls to update group location sharing settings with loading states and error handling.
 *
 * @returns {GroupLocationConfiguration} Hook return object containing saving state and update function
 */

export const useGroupLocationConfiguration = () => {
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();
  const { bearerToken } = useUserContext();
  const { organization } = useOrganizationUpdaterProvider();

  const updateGroupLocationTracking = useCallback(
    /**
     *
     * @param {Group} group
     * @param {boolean} allowLocationTracking
     * @returns
     */
    async (group, allowLocationTracking) => {
      /** @type {import('../../generated-client/models').OrganizationAdminUpdateGroupLocationTracking} */
      if (!organization?.org_code || !organization?.id) {
        console.error('org is missing org_code or id');
        showToast('Org does not exist. Please try refreshing your page');
        return;
      }
      const payload = {
        org_code: organization?.org_code,
        group_name: group.group_name,
        allow_group_location_tracking: allowLocationTracking,
        organization_azure_id: organization?.id,
      };

      try {
        setSaving(true);
        await updateGroupLocationTrackingViaAdmin(payload);
        showToast(
          t(StringKeys.LOCATION_SHARING_UPDATED_FOR_X, {
            x: group.group_name,
          }),
          ToastType.SUCCESS,
        );
        return { success: true };
      } catch (e) {
        showToast(
          t(ErrorStringKeys.FAILED_TO_UPDATE_LOCATION_SHARING_FOR_X, {
            x: group.group_name,
          }),
          ToastType.DANGER,
        );
        return { success: false };
      } finally {
        setSaving(false);
      }
    },
    [organization, bearerToken, showToast],
  );

  return { saving, updateGroupLocationTracking };
};
